import { Link2, Link2Off } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  getSubjectsForGrade,
  getStrandsForGradeSubject,
  getSpecificsForGradeSubject,
} from "../lib/curriculum";

/**
 * Combined Grades — two-column selection surface (LESSON mode only).
 *
 * One column per component grade of a split grade (e.g. 6/7). Each column
 * has its own subject selector (that grade's subjects only), strand
 * checkboxes, and specific-expectation checkboxes. Columns mirror by
 * default; the teacher can unlink them to edit each grade separately.
 *
 * This component is a VIEW/CONTROLLER over the existing reducer actions
 * (ADD_SUBJECT / REMOVE_SUBJECT / TOGGLE_STRAND / TOGGLE_EXPECTATION).
 * It never touches the reducer. `expectations[]` already carry a per-row
 * `grade` ("Grade 6"), which is what the submit payload's
 * `expectations_detail` forwards to the backend.
 *
 * Cap enforcement is DUPLICATED here (per founder ruling — logged as known
 * debt) rather than extracted into a shared helper. It reads the same
 * shared `state.expectations` count and the same grade-span-multiplied
 * `expectationCap`, so the budget is one shared total across the columns
 * and any optional Cross-Curricular extras.
 */
const gradeKey = (n) => `Grade ${n}`;

export default function CombinedGradeColumns({
  curriculum,
  splitGrades,
  state,
  dispatch,
  expectationCap,
  colSubject,
  setColSubject,
  linked,
  setLinked,
  fieldClass = "",
}) {
  const { subjects, strands, expectations } = state;

  const subjectsForNum = (n) =>
    curriculum ? getSubjectsForGrade(curriculum, gradeKey(n)) : [];
  const strandsFor = (n, subj) =>
    curriculum ? getStrandsForGradeSubject(curriculum, gradeKey(n), subj) : [];
  const specificsFor = (n, subj) =>
    curriculum ? getSpecificsForGradeSubject(curriculum, gradeKey(n), subj) : [];

  const totalChecked = expectations.length;
  const overCap = expectationCap > 0 && totalChecked >= expectationCap;

  // Mirror map. Only the 6/7 boundary needs the strand rule (lower grade
  // Social Studies ↔ upper grade History/Geography). Everything else is
  // subject-to-subject because both grades carry the same subject.
  const mirrorSubject = (subject, strandCode, toNum) => {
    const toSubs = subjectsForNum(toNum);
    if (toSubs.includes(subject)) return { subject, strand: null };
    if (subject === "Social Studies")
      return { subject: strandCode === "B" ? "Geography" : "History", strand: null };
    if (subject === "History") return { subject: "Social Studies", strand: "A" };
    if (subject === "Geography") return { subject: "Social Studies", strand: "B" };
    return null;
  };

  // Reconcile the reducer's flat subjects[] when a column's subject
  // changes. Removes the old subject only when the OTHER column no longer
  // uses it; otherwise unticks just this grade's expectations for it.
  const reconcileSubjectChange = (n, newSubj, working) => {
    const key = gradeKey(n);
    const oldSubj = working[n];
    if (oldSubj && oldSubj !== newSubj) {
      const partnerNum = splitGrades.find((g) => g !== String(n));
      const otherUses = working[partnerNum] === oldSubj;
      if (otherUses) {
        expectations
          .filter((e) => e.subject === oldSubj && (e.grade || "") === key)
          .forEach((e) =>
            dispatch({
              type: "TOGGLE_EXPECTATION",
              subject: e.subject,
              strand: e.strand,
              code: e.code,
              grade: e.grade,
            })
          );
      } else {
        dispatch({ type: "REMOVE_SUBJECT", subject: oldSubj });
      }
    }
    if (newSubj) dispatch({ type: "ADD_SUBJECT", subject: newSubj });
    return { ...working, [n]: newSubj || "" };
  };

  const handleSubjectChange = (n, newSubj) => {
    let working = reconcileSubjectChange(n, newSubj, { ...colSubject });
    if (linked && newSubj) {
      const partnerNum = splitGrades.find((g) => g !== String(n));
      const m = mirrorSubject(newSubj, null, partnerNum);
      if (m && m.subject) {
        working = reconcileSubjectChange(partnerNum, m.subject, working);
        if (
          m.strand &&
          !strands.some((s) => s.subject === m.subject && s.code === m.strand)
        ) {
          dispatch({ type: "TOGGLE_STRAND", subject: m.subject, code: m.strand });
        }
      }
    }
    setColSubject(working);
  };

  const handleToggleStrand = (n, subj, code) => {
    const wasSelected = strands.some(
      (s) => s.subject === subj && s.code === code
    );
    dispatch({ type: "TOGGLE_STRAND", subject: subj, code });
    // 6/7 boundary mirror: turning ON a Social Studies strand sets the
    // partner grade's subject (A → History, B → Geography).
    if (linked && !wasSelected && subj === "Social Studies") {
      const partnerNum = splitGrades.find((g) => g !== String(n));
      if (partnerNum && !subjectsForNum(partnerNum).includes("Social Studies")) {
        const partnerSubj = code === "B" ? "Geography" : "History";
        setColSubject(reconcileSubjectChange(partnerNum, partnerSubj, { ...colSubject }));
      }
    }
  };

  const handleToggleExpectation = (n, subj, strand, code) => {
    const key = gradeKey(n);
    const checked = expectations.some(
      (e) =>
        e.subject === subj &&
        e.strand === strand &&
        e.code === code &&
        (e.grade || "") === key
    );
    if (!checked && overCap) return;
    dispatch({ type: "TOGGLE_EXPECTATION", subject: subj, strand, code, grade: key });
  };

  const renderColumn = (n) => {
    const subj = colSubject[n] || "";
    const subjOptions = subjectsForNum(n);
    const selectedStrandCodes = subj
      ? strands.filter((s) => s.subject === subj).map((s) => s.code)
      : [];
    const allStrands = subj ? strandsFor(n, subj) : [];
    const specs = subj ? specificsFor(n, subj) : [];

    return (
      <div
        key={n}
        data-testid={`combined-column-${n}`}
        className="rounded-xl border border-[#96ded1]/60 bg-white/60 p-4 space-y-3"
      >
        <div className="flex items-center gap-2">
          <span className="h-6 px-2 grid place-items-center rounded-full bg-[#1c2d3d] text-white text-[11px] font-black uppercase tracking-wider">
            Grade {n}
          </span>
        </div>

        <div className="space-y-1.5">
          <label className="text-[#4a6984] font-bold text-[11px] uppercase tracking-[0.15em]">
            Subject for Grade {n}
          </label>
          <Select value={subj} onValueChange={(v) => handleSubjectChange(n, v)}>
            <SelectTrigger
              data-testid={`combined-subject-${n}`}
              className={fieldClass}
            >
              <SelectValue placeholder={`Pick a subject for Grade ${n}`} />
            </SelectTrigger>
            <SelectContent className="bg-white">
              {subjOptions.map((s) => (
                <SelectItem
                  key={s}
                  value={s}
                  data-testid={`combined-subject-${n}-option-${s
                    .replace(/\s+/g, "-")
                    .toLowerCase()}`}
                  className="text-[#1c2d3d] font-medium focus:bg-[#96ded1]/20"
                >
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {!subj ? (
          <p className="text-xs text-[#4a6984]/70 italic">
            Choose a subject to see its strands and expectations.
          </p>
        ) : (
          <>
            {/* Strands */}
            <div className="space-y-1.5">
              <p className="text-[10px] font-extrabold uppercase tracking-[0.18em] text-[#4a6984]/70">
                Strands
              </p>
              <div className="grid grid-cols-1 gap-1.5">
                {allStrands.map((s) => {
                  const checked = selectedStrandCodes.includes(s.code);
                  return (
                    <label
                      key={s.code}
                      className={`flex items-start gap-2.5 rounded px-2 py-1.5 cursor-pointer transition-colors ${
                        checked
                          ? "bg-[#96ded1]/30 border border-[#96ded1]"
                          : "bg-[#f4f5f7]/60 border border-transparent hover:border-[#4a6984]/20"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => handleToggleStrand(n, subj, s.code)}
                        data-testid={`combined-strand-${n}-${s.code.toLowerCase()}`}
                        className="mt-0.5 h-3.5 w-3.5 rounded border-[#4a6984]/40 accent-[#1c2d3d] shrink-0"
                      />
                      <span className="min-w-0 text-xs">
                        <span className="font-extrabold text-[#1c2d3d]">
                          {s.code}
                        </span>{" "}
                        <span className="text-[#4a6984]">{s.name}</span>
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Expectations, grouped by selected strand */}
            {selectedStrandCodes.length > 0 && (
              <div className="space-y-2">
                <p className="text-[10px] font-extrabold uppercase tracking-[0.18em] text-[#4a6984]/70">
                  Specific expectations
                </p>
                {selectedStrandCodes.map((strandCode) => {
                  const rows = specs.filter(
                    (r) => r.code && r.code.startsWith(strandCode)
                  );
                  if (rows.length === 0) return null;
                  const strandName =
                    allStrands.find((s) => s.code === strandCode)?.name ||
                    strandCode;
                  return (
                    <div
                      key={strandCode}
                      className="rounded-md bg-[#f4f5f7]/50 border border-[#96ded1]/30 px-3 py-2.5"
                      data-testid={`combined-exp-group-${n}-${strandCode.toLowerCase()}`}
                    >
                      <p className="font-extrabold text-[11px] text-[#1c2d3d] mb-1.5">
                        {strandCode} — {strandName}
                      </p>
                      <ul className="space-y-1.5">
                        {rows.map((row) => {
                          const checked = expectations.some(
                            (e) =>
                              e.subject === subj &&
                              e.strand === strandCode &&
                              e.code === row.code &&
                              (e.grade || "") === gradeKey(n)
                          );
                          const disabled = overCap && !checked;
                          return (
                            <li key={row.code}>
                              <label
                                className={`flex items-start gap-2.5 rounded px-2 py-1.5 transition-colors ${
                                  checked
                                    ? "bg-[#96ded1]/30 border border-[#96ded1]"
                                    : disabled
                                    ? "bg-[#f4f5f7]/40 border border-transparent opacity-50 cursor-not-allowed"
                                    : "bg-white/70 border border-transparent hover:border-[#4a6984]/20 cursor-pointer"
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  disabled={disabled}
                                  onChange={() =>
                                    handleToggleExpectation(
                                      n,
                                      subj,
                                      strandCode,
                                      row.code
                                    )
                                  }
                                  data-testid={`combined-exp-${n}-${row.code.toLowerCase()}`}
                                  className="mt-0.5 h-3.5 w-3.5 rounded border-[#4a6984]/40 accent-[#1c2d3d] shrink-0"
                                />
                                <span className="min-w-0 text-xs">
                                  <span className="font-extrabold text-[#1c2d3d]">
                                    {row.code}
                                  </span>
                                  {row.description ? (
                                    <span className="block text-[11px] text-[#4a6984]/80 leading-snug">
                                      {row.description}
                                    </span>
                                  ) : null}
                                </span>
                              </label>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-3" data-testid="combined-grade-columns">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-baseline gap-2">
          <span className="text-[#4a6984] font-bold text-xs uppercase tracking-[0.15em]">
            Curriculum for each grade
          </span>
          <span
            data-testid="combined-exp-count"
            className={`text-[10px] font-extrabold uppercase tracking-[0.15em] ${
              overCap ? "text-[#b45309]" : "text-[#1c2d3d]"
            }`}
          >
            {totalChecked}
            {expectationCap > 0 ? ` of ${expectationCap}` : ""} ticked
          </span>
        </div>
        <button
          type="button"
          onClick={() => setLinked((l) => !l)}
          data-testid="grade-mirror-toggle"
          aria-pressed={linked}
          className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-bold transition-colors ${
            linked
              ? "bg-[#1c2d3d] text-white"
              : "bg-white text-[#4a6984] border border-[#4a6984]/30"
          }`}
        >
          {linked ? (
            <Link2 className="h-3.5 w-3.5" />
          ) : (
            <Link2Off className="h-3.5 w-3.5" />
          )}
          {linked ? "Grades linked" : "Edit separately"}
        </button>
      </div>
      <p className="text-[11px] text-[#4a6984]/70 leading-relaxed">
        {linked
          ? "Pick on one side and the other keeps up. Tap \u201CGrades linked\u201D to edit each grade on its own."
          : "Each grade is independent. Tap \u201CEdit separately\u201D again to re-link them."}
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {splitGrades.map((n) => renderColumn(n))}
      </div>
    </div>
  );
}
